import { Pressable } from 'components/Pressable';
import { ScreenOverlay } from 'components/ScreenOverlay';
import { Txt } from 'components/Txt';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { useSelector } from 'hooks/useSelector';
import { theme } from 'lib/theme';
import React from 'react';
import Animated, {
  Easing,
  runOnJS,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { useDispatch } from 'react-redux';
import { doRemoveToast } from 'store/app/actions';
import { selectSnackText } from 'store/app/selectors';

export const DialogToast: React.FC = () => {
  const dispatch = useDispatch();
  const forceUpdate = useForceUpdate();
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout>>(null);
  const snackText = useSelector(selectSnackText);
  const snackTextRef = React.useRef<string>('');
  const snackIsErrorRef = React.useRef<boolean>(false);

  if (snackText) {
    snackTextRef.current = snackText.text;
    snackIsErrorRef.current = !!snackText.isError;
  }

  const showPosition = 6;
  const hidePosition = -55;
  const styleBottom = useSharedValue(hidePosition);

  const positionStyles = useAnimatedStyle(() => {
    return {
      bottom: styleBottom.value,
    };
  });

  React.useEffect(() => {
    if (snackText) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      timeoutRef.current = setTimeout(
        () => {
          dispatch(doRemoveToast());
          timeoutRef.current = null;
        },
        snackIsErrorRef.current ? 2000 : 1500,
      );
    }
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [dispatch, snackText]);

  const handleAnimationFinished = React.useCallback(() => {
    if (styleBottom.value === hidePosition) {
      forceUpdate();
    }
  }, [forceUpdate, hidePosition, styleBottom.value]);

  React.useEffect(() => {
    const newPosition = snackText ? showPosition : hidePosition;
    if (newPosition !== styleBottom.value) {
      styleBottom.value = withTiming(
        newPosition,
        {
          duration: 500,
          easing: Easing.out(Easing.exp),
        },
        () => {
          runOnJS(handleAnimationFinished)();
        },
      );
    }
  }, [
    showPosition,
    hidePosition,
    snackText,
    styleBottom,
    handleAnimationFinished,
  ]);

  if (!snackText && styleBottom.value === hidePosition) {
    return null;
  }

  return (
    <ScreenOverlay>
      <Animated.View
        style={[
          {
            position: 'absolute',
            alignItems: 'center',
            opacity: snackIsErrorRef.current ? 1 : 0.9,
            alignSelf: 'center',
            height: 50,
            zIndex: 1,
            pointerEvents: 'box-none',
          },
          positionStyles,
        ]}
      >
        <Pressable
          onPress={() => dispatch(doRemoveToast())}
          style={{
            backgroundColor: snackIsErrorRef.current
              ? theme.colors.red80
              : theme.colors.black80,
            borderRadius: 125,
            paddingHorizontal: theme.paddingHorizontal * 2,
            maxWidth: 300,
            justifyContent: 'center',
            height: 42,
            pointerEvents: 'auto',
          }}
        >
          <Txt
            style={{
              textAlign: 'center',
              color: theme.colors.white,
              overflow: 'visible',
            }}
            lineHeightNone
            numberOfLines={2}
          >
            {snackTextRef.current}
          </Txt>
        </Pressable>
      </Animated.View>
    </ScreenOverlay>
  );
};
